import { Edge2d, Point2d } from "./Geom2d";
import Geom2d from "./Geom2d";

export default class Shape2d {
    protected _vertices: Array<Point2d>;
    protected _leftBound: number = 0;
    protected _rightBound: number = 0;
    protected _topBound: number = 0;
    protected _bottomBound: number = 0;

    public constructor(vertices: Array<Point2d>) {
        this._vertices = vertices;

        this.updateBounds();
    }

    public clone(): Shape2d {
        return new Shape2d(this._vertices);
    }

    /** A list of individual points that make up this shape. */
    public get vertices() {
        return [...this._vertices];
    }
    public set vertices(val: Array<Point2d>) {
        this._vertices = val;
        this.updateBounds();
    }

    public get edges(): Array<Edge2d> {
        return this._vertices.flatMap((_, idx) => this.edge(idx) ?? []);
    }

    public get edgeCount(): number {
        return this._vertices.length;
    }

    public get leftBound(): number {
        return this._leftBound;
    }
    public get rightBound(): number {
        return this._rightBound;
    }
    public get topBound(): number {
        return this._topBound;
    }
    public get bottomBound(): number {
        return this._bottomBound;
    }

    /** Get the vertex at the specified index, or null if the index is out of bounds. */
    public vertex(index: number): Point2d | null {
        return index >= 0 && index < this._vertices.length
            ? this._vertices[index]
            : null;
    }

    public edge(index: number): Edge2d | null {
        if (
            this._vertices.length === 0 ||
            index < 0 ||
            index >= this._vertices.length
        ) {
            return null;
        }

        return {
            p1: this._vertices[index],
            p2:
                index === this._vertices.length - 1
                    ? this._vertices[0]
                    : this._vertices[index + 1],
        };
    }

    /** Get the first vertex of this shape. This vertex is both the start and end of the shape, and along with the last vertex, forms the shape's last edge. */
    public get firstVertex(): Point2d | null {
        return this._vertices.length === 0 ? null : this._vertices[0];
    }

    /** Gets the index of the last vertex of this shape, or -1 if the shape has no vertices. */
    public get lastVertexIndex(): number {
        return this._vertices.length - 1;
    }

    /** Get the last vertex of this shape. This vertex, along with the first vertex, forms the shape's last edge. */
    public get lastVertex(): Point2d | null {
        return this._vertices.length === 0
            ? null
            : this._vertices[this._vertices.length - 1];
    }

    /** Updates the position of the vertex at the specified index, without altering this shape's edge sequence. */
    public setVertexAt(index: number, vertex: Point2d): void {
        if (index >= 0 && index < this._vertices.length) {
            this._vertices[index] = vertex;

            this.updateBounds();
        }
    }

    /** Sets the position of the last vertex in this shape. This is effectively the same as calling setVertexAt for whatever index the final edge points to. */
    public setLastVertex(val: Point2d) {
        if (this._vertices.length === 0) {
            return;
        }

        this._vertices[this._vertices.length - 1] = val;
        this.updateBounds();
    }

    /** Adds a new vertex to the end of the vertex list, which will consequently update the edges and bounds of the shape. */
    public addVertex(vertex: Point2d): void {
        this._vertices.push(vertex);
        this.updateBounds();
    }

    public removeVertexAt(index: number): void {
        if (index < 0 || index >= this._vertices.length) {
            return;
        }
        this._vertices = this._vertices.filter((_, idx) => idx !== index);

        this.updateBounds();
    }

    /** Reassesses the outer boundaries of this shape. This is called anytime any relevant changes occur in the shape's vertices. */
    public updateBounds(): void {
        let left: number = Number.MAX_SAFE_INTEGER;
        let right: number = -Number.MAX_SAFE_INTEGER;
        let top: number = Number.MAX_SAFE_INTEGER;
        let bottom: number = -Number.MAX_SAFE_INTEGER;

        this._vertices.forEach((vertex) => {
            left = Math.min(left, vertex.x);
            right = Math.max(right, vertex.x);
            top = Math.min(top, vertex.y);
            bottom = Math.max(bottom, vertex.y);
        });

        this._leftBound = left;
        this._rightBound = right;
        this._topBound = top;
        this._bottomBound = bottom;
    }

    /** Returns true of the given point is contained within the geometry of this shape. */
    public containsPoint(point: Point2d): boolean {
        const testLine: Edge2d = {
            p1: {
                x: this.leftBound - 1,
                y: this.topBound - 1,
            },
            p2: {
                x: point.x,
                y: point.y,
            },
        };
        const intersectionCount = Geom2d.intersectionCount(
            testLine.p1.x,
            testLine.p1.y,
            testLine.p2.x,
            testLine.p2.y,
            ...this.edges
        );

        return intersectionCount > 0 && intersectionCount % 2 !== 0;
    }
}
